import * as WEBIFC from "web-ifc";
import type * as FRAGS from "@thatopen/fragments";

export const PRESENTATION_LAYERS = "PresentationLayers";
/** Classifier group name (same as the fragments index id). */
export const PRESENTATION_LAYERS_CLASSIFICATION = PRESENTATION_LAYERS;

type IfcApi = WEBIFC.IfcAPI;

const LAYER_TYPES = [
  WEBIFC.IFCPRESENTATIONLAYERASSIGNMENT,
  WEBIFC.IFCPRESENTATIONLAYERWITHSTYLE,
];

const SKIP_INDEX_TYPES = new Set([
  "IFCPROJECT",
  "IFCSITE",
  "IFCBUILDING",
  "IFCBUILDINGSTOREY",
  "IFCGRID",
  "IFCOPENINGELEMENT",
  "IFCSPATIALZONE",
  "IFCSPATIALELEMENT",
  "IFCSPATIALSTRUCTUREELEMENT",
  "IFCRELAGGREGATES",
  "IFCRELCONTAINEDINSPATIALSTRUCTURE",
  "IFCRELDEFINESBYPROPERTIES",
  "IFCRELASSOCIATES",
  "IFCRELDEFINESBYTYPE",
  "IFCPRESENTATIONLAYERASSIGNMENT",
  "IFCPRESENTATIONLAYERWITHSTYLE",
  "IFCPROPERTYSET",
  "IFCELEMENTQUANTITY",
  "IFCMATERIAL",
  "IFCMATERIALLIST",
  "IFCMATERIALLAYER",
  "IFCMATERIALLAYERSET",
  "IFCMATERIALLAYERSETUSAGE",
  "IFCSTYLEDITEM",
  "IFCPRESENTATIONSTYLEASSIGNMENT",
]);

interface RepresentationProductIndex {
  products: Set<number>;
  repToProducts: Map<number, Set<number>>;
  itemToRep: Map<number, number>;
}

const yieldToMainThread = () =>
  new Promise<void>((resolve) => setTimeout(resolve, 0));

function unwrapHandle(value: unknown): number | null {
  if (typeof value === "number") return value;
  if (value && typeof value === "object" && "value" in value) {
    const inner = (value as { value: unknown }).value;
    return typeof inner === "number" ? inner : null;
  }
  return null;
}

function unwrapLabel(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object" && "value" in value) {
    const inner = (value as { value: unknown }).value;
    return inner != null ? String(inner) : null;
  }
  return String(value);
}

function addRepProduct(
  index: RepresentationProductIndex,
  repId: number,
  productId: number,
) {
  let set = index.repToProducts.get(repId);
  if (!set) {
    set = new Set();
    index.repToProducts.set(repId, set);
  }
  set.add(productId);
}

function registerProductRepresentations(
  api: IfcApi,
  modelID: number,
  productId: number,
  index: RepresentationProductIndex,
) {
  const product = api.GetLine(modelID, productId);
  if (!product?.Representation) return;

  index.products.add(productId);

  const prodRepId = unwrapHandle(product.Representation);
  if (prodRepId == null) return;

  const prodRep = api.GetLine(modelID, prodRepId);
  const representations = prodRep?.Representations as unknown[] | undefined;
  if (!Array.isArray(representations)) return;

  for (const repHandle of representations) {
    const shapeId = unwrapHandle(repHandle);
    if (shapeId == null) continue;

    addRepProduct(index, shapeId, productId);

    const shape = api.GetLine(modelID, shapeId);
    const items = shape?.Items as unknown[] | undefined;
    if (!Array.isArray(items)) continue;

    for (const itemHandle of items) {
      const itemId = unwrapHandle(itemHandle);
      if (itemId == null) continue;

      index.itemToRep.set(itemId, shapeId);

      const item = api.GetLine(modelID, itemId);
      const mappedRepId = unwrapHandle(item?.MappedRepresentation);
      if (mappedRepId != null) {
        addRepProduct(index, mappedRepId, productId);
      }
    }
  }
}

function buildRepresentationProductIndex(
  api: IfcApi,
  modelID: number,
): RepresentationProductIndex {
  const index: RepresentationProductIndex = {
    products: new Set(),
    repToProducts: new Map(),
    itemToRep: new Map(),
  };

  const types = api.GetAllTypesOfModel(modelID);
  for (const entry of types) {
    const typeName = entry.typeName?.toUpperCase?.() ?? "";
    if (SKIP_INDEX_TYPES.has(typeName)) continue;
    if (!typeName.startsWith("IFC")) continue;

    const ids = api.GetLineIDsWithType(modelID, entry.typeID);
    for (let i = 0; i < ids.size(); i++) {
      registerProductRepresentations(api, modelID, ids.get(i), index);
    }
  }

  return index;
}

function expandAssignedItemIds(
  api: IfcApi,
  modelID: number,
  assignedItems: unknown[],
  out: Set<number>,
  visited = new Set<number>(),
) {
  for (const handle of assignedItems) {
    const id = unwrapHandle(handle);
    if (id == null || visited.has(id)) continue;
    visited.add(id);
    out.add(id);

    const line = api.GetLine(modelID, id);
    if (!line) continue;

    const items = line.Items as unknown[] | undefined;
    if (Array.isArray(items)) {
      expandAssignedItemIds(api, modelID, items, out, visited);
    }

    const mappedRepId = unwrapHandle(line.MappedRepresentation);
    if (mappedRepId != null && !visited.has(mappedRepId)) {
      visited.add(mappedRepId);
      out.add(mappedRepId);
      const mappedLine = api.GetLine(modelID, mappedRepId);
      const mappedItems = mappedLine?.Items as unknown[] | undefined;
      if (Array.isArray(mappedItems)) {
        expandAssignedItemIds(api, modelID, mappedItems, out, visited);
      }
    }
  }
}

function collectProductsForAssignedSet(
  index: RepresentationProductIndex,
  assignedIds: Set<number>,
): Set<number> {
  const products = new Set<number>();

  for (const assignedId of assignedIds) {
    if (index.products.has(assignedId)) {
      products.add(assignedId);
    }

    const fromRep = index.repToProducts.get(assignedId);
    if (fromRep) {
      for (const productId of fromRep) products.add(productId);
    }

    const shapeRepId = index.itemToRep.get(assignedId);
    if (shapeRepId != null) {
      const fromItem = index.repToProducts.get(shapeRepId);
      if (fromItem) {
        for (const productId of fromItem) products.add(productId);
      }
    }
  }

  return products;
}

function buildExpressToGuidMap(
  api: IfcApi,
  modelID: number,
  layers: Map<string, Set<number>>,
): Map<number, string> {
  const expressToGuid = new Map<number, string>();
  const productIds = new Set<number>();

  for (const expressIds of layers.values()) {
    for (const expressId of expressIds) productIds.add(expressId);
  }

  for (const expressId of productIds) {
    const line = api.GetLine(modelID, expressId);
    const guid = unwrapLabel(line?.GlobalId);
    if (guid) expressToGuid.set(expressId, guid);
  }

  return expressToGuid;
}

export async function extractPresentationLayersFromIfc(
  api: IfcApi,
  modelID: number,
): Promise<{
  layers: Map<string, Set<number>>;
  expressToGuid: Map<number, string>;
}> {
  const layers = new Map<string, Set<number>>();
  const layerExpressIds: number[] = [];

  for (const type of LAYER_TYPES) {
    const ids = api.GetLineIDsWithType(modelID, type, true);
    for (let i = 0; i < ids.size(); i++) {
      layerExpressIds.push(ids.get(i));
    }
  }

  await yieldToMainThread();
  const repIndex = buildRepresentationProductIndex(api, modelID);
  await yieldToMainThread();

  for (let i = 0; i < layerExpressIds.length; i++) {
    const layerExpressId = layerExpressIds[i];
    const layer = api.GetLine(modelID, layerExpressId);
    const layerName = unwrapLabel(layer?.Name);
    if (!layerName) continue;

    const assignedItems = layer?.AssignedItems as unknown[] | undefined;
    if (!Array.isArray(assignedItems) || !assignedItems.length) continue;

    const assignedIds = new Set<number>();
    expandAssignedItemIds(api, modelID, assignedItems, assignedIds);
    const products = collectProductsForAssignedSet(repIndex, assignedIds);
    if (!products.size) continue;

    const existing = layers.get(layerName) ?? new Set<number>();
    for (const productId of products) existing.add(productId);
    layers.set(layerName, existing);

    if (i > 0 && i % 25 === 0) {
      await yieldToMainThread();
    }
  }

  const expressToGuid = buildExpressToGuidMap(api, modelID, layers);
  return { layers, expressToGuid };
}

export function layerExpressMapToLocalIds(
  layers: Map<string, Set<number>>,
  expressToGuid: Map<number, string>,
  guidToLocal: Map<string, number>,
): Map<string, number[]> {
  const result = new Map<string, number[]>();

  for (const [layerName, expressIds] of layers) {
    const localIds: number[] = [];
    for (const expressId of expressIds) {
      const guid = expressToGuid.get(expressId);
      if (!guid) continue;
      const localId = guidToLocal.get(guid);
      if (localId != null) localIds.push(localId);
    }
    if (localIds.length) result.set(layerName, localIds);
  }

  return result;
}

export async function buildGuidToLocalIdMap(
  model: FRAGS.FragmentsModel,
): Promise<Map<string, number>> {
  const guidToLocal = new Map<string, number>();
  const localIds = await model.getItemsIdsWithGeometry();
  if (!localIds.length) return guidToLocal;

  const guids = await model.getGuidsByLocalIds(localIds);
  for (let i = 0; i < localIds.length; i++) {
    const guid = guids[i];
    if (guid) guidToLocal.set(guid, localIds[i]);
  }
  return guidToLocal;
}

export function buildPresentationLayersIndexData(
  layerToLocalIds: Map<string, number[]>,
): FRAGS.RawIndexData {
  const keys: string[] = [];
  const values: number[] = [];
  const end: number[] = [];

  const sortedNames = [...layerToLocalIds.keys()].sort((a, b) =>
    a.localeCompare(b),
  );

  for (const name of sortedNames) {
    const ids = layerToLocalIds.get(name)!;
    keys.push(name);
    for (const id of ids) values.push(id);
    end.push(values.length);
  }

  return {
    name: PRESENTATION_LAYERS,
    keys,
    values,
    end,
  };
}

const invertedLayerCache = new WeakMap<
  FRAGS.FragmentsModel,
  Promise<Map<number, string[]>>
>();

/**
 * Clears the per-model presentation-layer lookup cache. Call this whenever the
 * model's layer index is (re)built so subsequent reads pick up fresh data.
 */
export function invalidatePresentationLayersCache(
  model: FRAGS.FragmentsModel,
): void {
  invertedLayerCache.delete(model);
}

async function buildInvertedLayerMap(
  model: FRAGS.FragmentsModel,
): Promise<Map<number, string[]>> {
  const layerMap = await readPresentationLayersFromModel(model);
  const inverted = new Map<number, string[]>();

  for (const [layerName, ids] of Array.from(layerMap)) {
    for (const id of ids) {
      const existing = inverted.get(id);
      if (existing) existing.push(layerName);
      else inverted.set(id, [layerName]);
    }
  }

  return inverted;
}

function getInvertedLayerMap(
  model: FRAGS.FragmentsModel,
): Promise<Map<number, string[]>> {
  let cached = invertedLayerCache.get(model);
  if (!cached) {
    cached = buildInvertedLayerMap(model).catch((error) => {
      invertedLayerCache.delete(model);
      throw error;
    });
    invertedLayerCache.set(model, cached);
  }
  return cached;
}

export async function getPresentationLayersForElement(
  model: FRAGS.FragmentsModel,
  localId: number,
): Promise<string[]> {
  const inverted = await getInvertedLayerMap(model);
  const names = inverted.get(localId);
  if (!names) return [];
  return [...names].sort((a, b) => a.localeCompare(b));
}

export async function readPresentationLayersFromModel(
  model: FRAGS.FragmentsModel,
): Promise<Map<string, number[]>> {
  const layers = new Map<string, number[]>();
  const keys = await model.getIndexKeys(PRESENTATION_LAYERS);
  if (!keys || !Array.isArray(keys)) return layers;

  for (const layerName of keys) {
    if (typeof layerName !== "string") continue;
    const entry = await model.getIndexEntry(PRESENTATION_LAYERS, layerName);
    if (entry instanceof Uint32Array) {
      layers.set(layerName, [...entry]);
    } else if (Array.isArray(entry)) {
      layers.set(
        layerName,
        (entry as unknown[]).filter(
          (id): id is number => typeof id === "number",
        ),
      );
    }
  }

  return layers;
}
